import React from 'react';
import { Segmented } from 'antd';
import { DatabaseOutlined, HomeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { TransportRouteDetailDto } from '../../../../dto/transport';
import { deriveTransportKind, transportKindLabelKey, type OrderDeliveryMode, type TransportKind } from '../../../transports/transportDirection';
import { getVisibleOrderTransports } from '../../orderVisibleTransports';
import { getTransportLabel } from '../../../../utils/transportLabel';
import { formatMoneyLocalized } from '../../../../utils/currency';
import { roundMoney } from '../../../../utils/money';
import type { OrderGuideStepKey } from './orderGuideRules';
import './OrderGuide.css';

/** Entries shown before the „+N więcej” line (WZ products, trucks of one kind). */
const VISIBLE_ENTRIES = 5;
/** Up to this many entries are listed in full; more than this are cut to `VISIBLE_ENTRIES`. */
const LIST_LIMIT = 6;

export interface WzProductGroup {
  name: string;
  /** Dimensions of the product („gr. 12 x szer. 1250 x dł. 3000 mm”); absent when the line has none. */
  dimensions?: string;
  /** Number of order lines with this product name (and dimensions). */
  lines: number;
}

/**
 * Groups line names by product, keeping the order of first occurrence. Lines
 * of the same name with different dimensions stay separate entries.
 */
export function groupWzProducts(lineNames: string[], lineDimensions: Array<string | null | undefined> = []): WzProductGroup[] {
  const groups = new Map<string, WzProductGroup>();
  lineNames.forEach((name, index) => {
    const dimensions = lineDimensions[index] || undefined;
    const key = `${name}\u0000${dimensions ?? ''}`;
    const group = groups.get(key);
    if (group) group.lines += 1;
    else groups.set(key, dimensions ? { name, dimensions, lines: 1 } : { name, lines: 1 });
  });
  return Array.from(groups.values());
}

export interface OrderPlanAsideProps {
  step: OrderGuideStepKey;
  deliveryMode: OrderDeliveryMode;
  /** Names of the lines — „Źródło na WZ” says for each that it comes from the warehouse. */
  lineNames: string[];
  /** Dimensions of the same lines (same order as `lineNames`), shown under each name. */
  lineDimensions?: Array<string | null | undefined>;
  /** Who may change „Dostawa” on the card from Potwierdzone on (`canEditDeliveryMode`, not read-only). */
  canChangeDelivery: boolean;
  onDeliveryModeChange?: (mode: OrderDeliveryMode) => void;
  savingDelivery?: boolean;
  /** „Skąd realizujemy?” chosen at the confirmation (information for Logistyk); absent = the warehouse. */
  fulfillmentSource?: 'WAREHOUSE' | 'FACTORY_DIRECT';
  /** The Handlowiec's message to Logistyk (Valentyna). */
  fulfillmentNote?: string | null;
  /** The ZK's transports; null = not visible to this role (no „bez auta” claim). */
  transports?: TransportRouteDetailDto[] | null;
  transportsLoading?: boolean;
  /** This ZK: the freight share of each truck is read from its route order for this id. */
  orderId?: string | null;
  /** `canViewTransportCosts` (the ZK card's transport-cost gate); false = no amounts, no sum. */
  canViewCosts?: boolean;
}

export interface TransportKindGroup {
  /** Null = the kind cannot be determined (no direction on the truck). */
  kind: TransportKind | null;
  /** Visible trucks of this kind, in „Dostawy w trasie” order. */
  transports: TransportRouteDetailDto[];
}

/**
 * „Rodzaj auta” of a ZK truck, derived like the backend does (no stored kind on
 * the route DTO): direction + the customer and supplier orders on board. Null
 * when the direction is unknown.
 */
export function transportKindOf(transport: TransportRouteDetailDto): TransportKind | null {
  if (!transport.direction) return null;
  return deriveTransportKind({
    direction: transport.direction,
    customerOrderCount: transport.routeOrders?.length ?? 0,
    supplierOrderCount: transport.supplierOrders?.length ?? 0,
  });
}

const finiteOrNull = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);

/**
 * The amount shown next to a truck, PLN canonical, read from the stored data:
 * - outbound („Magazyn → klient”): this ZK's part, `routeOrders[].allocatedTransportCost`;
 * - inbound („Fabryka → klient”, „Fabryka → magazyn”): the truck's whole freight,
 *   the sum of `supplierOrders[].freightShare.amountPLN` (it goes into the ZD batch;
 *   `transportCost` is not used — it is 0 on these trucks).
 * Null = the field is not in the data (redacted or missing) or the kind is unknown;
 * null is never shown as 0.
 */
export function truckFreightPLN(transport: TransportRouteDetailDto, orderId: string): number | null {
  const kind = transportKindOf(transport);
  if (kind === 'WAREHOUSE_TO_CUSTOMER') {
    return finiteOrNull(transport.routeOrders?.find((routeOrder) => routeOrder.orderId === orderId)?.allocatedTransportCost);
  }
  if (kind === 'FACTORY_TO_CUSTOMER' || kind === 'FACTORY_TO_WAREHOUSE') {
    const orders = transport.supplierOrders ?? [];
    if (orders.length === 0) return null;
    const amounts = orders.map((order) => finiteOrNull(order.freightShare?.amountPLN));
    if (amounts.some((amount) => amount === null)) return null;
    return roundMoney(amounts.reduce<number>((sum, amount) => sum + (amount as number), 0));
  }
  return null;
}

/** An inbound truck carrying more than one customer order: its amount is the whole truck, not this ZK's part. */
export function isWholeTruckAmount(transport: TransportRouteDetailDto): boolean {
  const kind = transportKindOf(transport);
  return (kind === 'FACTORY_TO_CUSTOMER' || kind === 'FACTORY_TO_WAREHOUSE') && (transport.routeOrders?.length ?? 0) > 1;
}

/** Trucks grouped by „Rodzaj auta”, groups and trucks kept in the order they come in. */
export function groupTransportsByKind(transports: TransportRouteDetailDto[]): TransportKindGroup[] {
  const groups = new Map<TransportKind | null, TransportRouteDetailDto[]>();
  for (const transport of transports) {
    const kind = transportKindOf(transport);
    groups.set(kind, [...(groups.get(kind) ?? []), transport]);
  }
  return Array.from(groups, ([kind, list]) => ({ kind, transports: list }));
}

/**
 * „Sposób realizacji” of the first mockup (`plan`): the one source (the
 * warehouse, full FIFO — decision 2026-10-04 pkt 1, so nothing to choose), the
 * delivery to the customer, where each line is issued from on the WZ and the
 * transport that follows from the delivery. From Potwierdzone the delivery is
 * a switch (the same command as on „Gotowe do wydania”); before that it is
 * read-only and says where to change it. The source line shows what the
 * Handlowiec chose at the confirmation (owner 2026-10-05: „Z magazynu (FIFO)”
 * or „Prosto z fabryki do klienta”, plus his message to Logistyk) — information
 * only: the WZ below still issues from the warehouse. When a „Fabryka → klient”
 * truck carries the order (owner 2026-10-10) the warehouse source reads
 * „Wirtualny magazyn (FIFO)”: the goods go straight to the customer, the
 * warehouse is only the stock accounting (batches and FIFO) WZ takes them from.
 */
export default function OrderPlanAside({
  step, deliveryMode, lineNames, lineDimensions, canChangeDelivery, onDeliveryModeChange, savingDelivery, fulfillmentSource, fulfillmentNote,
  transports, transportsLoading, orderId, canViewCosts,
}: OrderPlanAsideProps) {
  const { t, i18n } = useTranslation(['orders', 'transport']);
  const showAmounts = Boolean(canViewCosts && orderId);
  const money = (value: number) => formatMoneyLocalized(value, 'PLN', i18n.language);
  const pickup = deliveryMode === 'CUSTOMER_PICKUP';
  const deliveryLabel = t(`orders:deliveryMode.options.${deliveryMode}`);
  const factoryDirect = fulfillmentSource === 'FACTORY_DIRECT';
  const canSwitch = step === 'CONFIRMED' && canChangeDelivery && Boolean(onDeliveryModeChange);
  const groups = groupWzProducts(lineNames, lineDimensions);
  const visibleTransports = transports && !transportsLoading ? getVisibleOrderTransports(transports) : null;
  const kindGroups = visibleTransports ? groupTransportsByKind(visibleTransports) : [];
  // The same fact as the „Fabryka → klient” header in „Transport” below.
  const virtualWarehouse = !factoryDirect && kindGroups.some((group) => group.kind === 'FACTORY_TO_CUSTOMER');
  return (
    <aside className="zk-plan" aria-label={t('orders:process.guide.plan.title')} data-testid="zk-guide-plan">
      <div className="zk-plan__head">
        <span>{t('orders:process.guide.plan.title')}</span>
        <span className="zk-plan__tag">{t('orders:process.guide.plan.tag')}</span>
      </div>
      <div className="zk-plan__source" data-testid="zk-guide-source">
        {virtualWarehouse ? <DatabaseOutlined aria-hidden="true" /> : <HomeOutlined aria-hidden="true" />}
        {t(factoryDirect
          ? 'orders:process.guide.plan.sourceFactory'
          : virtualWarehouse ? 'orders:process.guide.plan.sourceVirtual' : 'orders:process.guide.plan.source')}
      </div>
      {virtualWarehouse && (
        <span className="zk-plan__label" data-testid="zk-guide-source-hint">{t('orders:process.guide.plan.sourceVirtualHint')}</span>
      )}
      {fulfillmentNote && (
        <div className="zk-plan__row">
          <span className="zk-plan__label">{t('orders:process.guide.plan.sourceMessage')}</span>
          <b className="zk-plan__fact" data-testid="zk-guide-source-message" style={{ whiteSpace: 'pre-wrap' }}>{fulfillmentNote}</b>
        </div>
      )}
      {factoryDirect ? (
        <div className="zk-plan__row zk-plan__row--rule">
          <span className="zk-plan__label">{t('orders:process.guide.plan.transport')}</span>
          <b className="zk-plan__fact" data-testid="zk-guide-transport-factory">{t('orders:process.guide.plan.transportFactory')}</b>
        </div>
      ) : (
        <>
      <div className="zk-plan__row">
        <span className="zk-plan__label">{t('orders:process.guide.plan.delivery')}</span>
        {canSwitch ? (
          <Segmented<OrderDeliveryMode>
            size="small"
            value={deliveryMode}
            disabled={savingDelivery}
            options={[
              { label: t('orders:deliveryMode.options.OUR_DELIVERY'), value: 'OUR_DELIVERY' },
              { label: t('orders:deliveryMode.options.CUSTOMER_PICKUP'), value: 'CUSTOMER_PICKUP' },
            ]}
            onChange={(value) => onDeliveryModeChange?.(value as OrderDeliveryMode)}
            aria-label={t('orders:process.guide.plan.delivery')}
            data-testid="zk-guide-delivery-switch"
          />
        ) : (
          <>
            <b className="zk-plan__value" data-testid="zk-guide-delivery">{deliveryLabel}</b>
            <span className="zk-plan__label">{t(step === 'DRAFT' ? 'orders:process.guide.plan.deliveryChangeDraft' : step === 'OFFER' ? 'orders:process.guide.plan.deliveryChangeOffer' : 'orders:process.guide.plan.deliveryReadOnly')}</span>
          </>
        )}
      </div>
      <div className="zk-plan__row zk-plan__row--rule">
        <span className="zk-plan__label">
          {t('orders:process.guide.plan.wz')} <span className="zk-plan__chip">{t(virtualWarehouse ? 'orders:process.guide.plan.wzVirtual' : 'orders:process.guide.plan.wzAlways')}</span>
        </span>
        {lineNames.length ? (
          <ul className="zk-plan__list" data-testid="zk-guide-wz-lines">
            {(groups.length > LIST_LIMIT ? groups.slice(0, VISIBLE_ENTRIES) : groups).map((group) => (
              <li key={`${group.name}\u0000${group.dimensions ?? ''}`} className="zk-plan__fact">
                {group.lines > 1
                  ? t('orders:process.guide.plan.wzProduct', { name: group.name, count: group.lines })
                  : group.name}
                {group.dimensions && <small className="zk-plan__dimensions" data-testid="zk-guide-wz-line-dimensions">{group.dimensions}</small>}
              </li>
            ))}
            {groups.length > LIST_LIMIT && (
              <li className="zk-plan__more" data-testid="zk-guide-wz-more">
                {t('orders:process.guide.plan.moreItems', { count: groups.length - VISIBLE_ENTRIES })}
              </li>
            )}
          </ul>
        ) : (
          <b className="zk-plan__fact">—</b>
        )}
      </div>
      <div className="zk-plan__row zk-plan__row--rule">
        <span className="zk-plan__label">{t('orders:process.guide.plan.transport')}</span>
        {visibleTransports && visibleTransports.length > 0 ? (
          <>
            <div className="zk-plan__trucks" data-testid="zk-guide-transport-summary">
              {kindGroups.map((group) => {
                const shown = group.transports.length > LIST_LIMIT ? group.transports.slice(0, VISIBLE_ENTRIES) : group.transports;
                const label = group.kind ? t(transportKindLabelKey(group.kind)) : t('orders:process.guide.plan.transport');
                // The sum only when every truck of the kind has its share in the data; a partial sum would read as the total.
                const shares = showAmounts ? group.transports.map((transport) => truckFreightPLN(transport, orderId as string)) : [];
                const kindSum = shares.length > 0 && shares.every((share) => share !== null)
                  ? roundMoney(shares.reduce<number>((sum, share) => sum + (share as number), 0))
                  : null;
                return (
                  <div key={group.kind ?? 'unknown'} className="zk-plan__kind">
                    <div className="zk-plan__fact" data-testid="zk-guide-transport-kind-header">
                      {kindSum !== null
                        ? t('orders:process.guide.plan.transportTrucksSum', { label, count: group.transports.length, sum: money(kindSum) })
                        : t('orders:process.guide.plan.transportTrucks', { label, count: group.transports.length })}
                    </div>
                    <ul className="zk-plan__list" data-testid="zk-guide-transport-trucks">
                      {shown.map((transport) => {
                        const carrier = transport.transportCompany?.trim();
                        const share = showAmounts ? truckFreightPLN(transport, orderId as string) : null;
                        return (
                          <li key={transport.id} className="zk-plan__truck-row">
                            <Link to={`/app/transports/${encodeURIComponent(transport.id)}`} className="zk-plan__truck" data-testid="zk-guide-transport-link">
                              {carrier ? <>{carrier} · </> : null}
                              <span className="zk-plan__number">{getTransportLabel(transport, '—')}</span>
                            </Link>
                            {share !== null && (
                              <span className="zk-plan__amount-col">
                                <span className="zk-plan__amount" data-testid="zk-guide-transport-amount">{money(share)}</span>
                                {isWholeTruckAmount(transport) && (
                                  <span className="zk-plan__more" data-testid="zk-guide-transport-whole">{t('orders:process.guide.plan.wholeTruck')}</span>
                                )}
                              </span>
                            )}
                          </li>
                        );
                      })}
                      {group.transports.length > LIST_LIMIT && (
                        <li className="zk-plan__more" data-testid="zk-guide-transport-more">
                          {t('orders:process.guide.plan.moreItems', { count: group.transports.length - VISIBLE_ENTRIES })}
                        </li>
                      )}
                    </ul>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <b className="zk-plan__fact">{t(pickup ? 'orders:process.guide.plan.transportPickup' : 'orders:process.guide.plan.transportOut')}</b>
            {visibleTransports && (
              <span className="zk-plan__more" data-testid="zk-guide-transport-none">{t('orders:process.guide.plan.transportNone')}</span>
            )}
          </>
        )}
      </div>
        </>
      )}
    </aside>
  );
}
